export type ProgrammeRecord = {
  project: string;
  phase: string | null;
  start: string; // "Mon-YY"
  end: string; // "Mon-YY"
  status: string;
};

export type RecordMatch = {
  oldRecord: ProgrammeRecord;
  newRecord: ProgrammeRecord;
  score: number;
};

export type ManualMatchRule = {
  old: { project: string; phase: string | null };
  new: { project: string; phase: string | null };
};

export type MatchOptions = {
  /**
   * Manual override for known renames / merges between snapshots.
   * Applied before fuzzy matching and treated as locked one-to-one pairs.
   */
  manualMatches?: ManualMatchRule[];
};

export type MatchResult = {
  matches: RecordMatch[];
  added: ProgrammeRecord[]; // present only in the newer snapshot
  removed: ProgrammeRecord[]; // present only in the older snapshot
};

export type MatchDiff = {
  deltaStartMonths: number;
  deltaEndMonths: number;
  slippedStart: boolean;
  slippedEnd: boolean;
  statusChanged: boolean;
};

const MONTH_INDEX: Record<string, number> = {
  Jan: 0,
  Feb: 1,
  Mar: 2,
  Apr: 3,
  May: 4,
  Jun: 5,
  Jul: 6,
  Aug: 7,
  Sep: 8,
  Oct: 9,
  Nov: 10,
  Dec: 11,
};

export function monthIndex(token: string): number {
  // Expected format: "Mon-YY" e.g. "Feb-25"
  const m = /^([A-Za-z]{3})-(\d{2})$/.exec(token.trim());
  if (!m) {
    throw new Error(`Invalid month token: ${token}`);
  }

  const mon = m[1];
  const yy = Number(m[2]);
  const month = MONTH_INDEX[mon];
  if (month === undefined || Number.isNaN(yy)) {
    throw new Error(`Invalid month token: ${token}`);
  }

  const year = 2000 + yy;
  return year * 12 + month;
}

function normalizeText(value: string): string {
  return (value ?? "")
    .toLowerCase()
    // Council PDFs often use en-dash/em-dash for separators.
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

function bigramCounts(value: string): Map<string, number> {
  const padded = ` ${value} `;
  const counts = new Map<string, number>();
  for (let i = 0; i < padded.length - 1; i += 1) {
    const gram = padded.slice(i, i + 2);
    counts.set(gram, (counts.get(gram) ?? 0) + 1);
  }
  return counts;
}

function totalBigrams(value: string): number {
  const padded = ` ${value} `;
  return Math.max(0, padded.length - 1);
}

/**
 * Bigram multiset Dice coefficient, tolerant to truncation and minor typos.
 */
export function diceCoefficient(a: string, b: string): number {
  const na = normalizeText(a);
  const nb = normalizeText(b);

  if (na === nb) return 1;
  if (!na || !nb) return 0;

  const ca = bigramCounts(na);
  const cb = bigramCounts(nb);

  let intersection = 0;
  for (const [gram, countA] of ca.entries()) {
    const countB = cb.get(gram) ?? 0;
    intersection += Math.min(countA, countB);
  }

  const denom = totalBigrams(na) + totalBigrams(nb);
  if (denom === 0) return 0;
  return (2 * intersection) / denom;
}

export function pairScore(oldRec: ProgrammeRecord, newRec: ProgrammeRecord): number {
  const projectSim = diceCoefficient(oldRec.project, newRec.project);
  if (projectSim < 0.8) return 0;

  const phaseSim = diceCoefficient(oldRec.phase ?? "", newRec.phase ?? "");

  const deltaStart = monthIndex(newRec.start) - monthIndex(oldRec.start);
  const deltaEnd = monthIndex(newRec.end) - monthIndex(oldRec.end);
  const startSim = 1 - Math.min(Math.abs(deltaStart) / 24, 1);
  const endSim = 1 - Math.min(Math.abs(deltaEnd) / 24, 1);

  return projectSim * 0.7 + phaseSim * 0.12 + startSim * 0.1 + endSim * 0.08;
}

export function matchReports(
  oldRecords: ProgrammeRecord[],
  newRecords: ProgrammeRecord[],
  options: MatchOptions = {}
): MatchResult {
  const usedOld = new Set<number>();
  const usedNew = new Set<number>();
  const matches: RecordMatch[] = [];

  const manualMatches = options.manualMatches ?? [];
  if (manualMatches.length > 0) {
    for (const rule of manualMatches) {
      const oldIdx = oldRecords.findIndex(
        (r) =>
          normalizeText(r.project) === normalizeText(rule.old.project) &&
          (r.phase ?? null) === (rule.old.phase ?? null),
      );
      const newIdx = newRecords.findIndex(
        (r) =>
          normalizeText(r.project) === normalizeText(rule.new.project) &&
          (r.phase ?? null) === (rule.new.phase ?? null),
      );

      if (oldIdx < 0 || newIdx < 0) continue;
      if (usedOld.has(oldIdx) || usedNew.has(newIdx)) continue;

      usedOld.add(oldIdx);
      usedNew.add(newIdx);
      matches.push({
        oldRecord: oldRecords[oldIdx],
        newRecord: newRecords[newIdx],
        score: 1,
      });
    }
  }

  const candidates: Array<{ score: number; oldIndex: number; newIndex: number }> = [];

  for (let i = 0; i < oldRecords.length; i += 1) {
    if (usedOld.has(i)) continue;
    for (let j = 0; j < newRecords.length; j += 1) {
      if (usedNew.has(j)) continue;
      const projectSim = diceCoefficient(oldRecords[i].project, newRecords[j].project);
      if (projectSim < 0.8) continue;

      const score = pairScore(oldRecords[i], newRecords[j]);
      if (score <= 0) continue;

      candidates.push({ score, oldIndex: i, newIndex: j });
    }
  }

  candidates.sort((a, b) => b.score - a.score);

  for (const c of candidates) {
    if (c.score < 0.78) break;
    if (usedOld.has(c.oldIndex) || usedNew.has(c.newIndex)) continue;

    usedOld.add(c.oldIndex);
    usedNew.add(c.newIndex);
    matches.push({
      oldRecord: oldRecords[c.oldIndex],
      newRecord: newRecords[c.newIndex],
      score: c.score,
    });
  }

  const added = newRecords.filter((_, idx) => !usedNew.has(idx));
  const removed = oldRecords.filter((_, idx) => !usedOld.has(idx));

  return { matches, added, removed };
}

export function computeMatchDiff(match: RecordMatch): MatchDiff {
  const deltaStartMonths = monthIndex(match.newRecord.start) - monthIndex(match.oldRecord.start);
  const deltaEndMonths = monthIndex(match.newRecord.end) - monthIndex(match.oldRecord.end);

  return {
    deltaStartMonths,
    deltaEndMonths,
    slippedStart: deltaStartMonths > 0,
    slippedEnd: deltaEndMonths > 0,
    statusChanged: normalizeStatus(match.oldRecord.status) !== normalizeStatus(match.newRecord.status),
  };
}

export function recordLabel(rec: ProgrammeRecord): string {
  return rec.phase ? `${rec.project} \u00b7 ${rec.phase}` : rec.project;
}

function normalizeStatus(value: string | null | undefined): string {
  const normalized = normalizeText(value ?? "");

  if (
    normalized === "this project is under construction" ||
    normalized === "construction in progress"
  ) {
    return "project is under construction";
  }

  if (normalized === "construction not yet started") {
    return "construction for this project has not yet started";
  }

  return normalized;
}
